import type { OdinDataSort } from '@myosh/odin-components';
import { createApi } from '@reduxjs/toolkit/query/react';
import { format, isValid, parse } from 'date-fns';
import { enGB } from 'date-fns/locale';
import { baseQueryWithReAuth } from './api';
import { getFilters } from './get-filters';
import { getSortedFields } from './get-sortedFields';
import type { TableQueryParameters } from './table-data';
import type { FieldFiltersProps, TableDataColumn } from './table-data.interfaces';

export interface DetailsWindowQueryParameters extends TableQueryParameters {
  field?: string;
  fieldValue?: string;
  secondField?: string;
  secondFieldValue?: string;
  sortedFields?: Record<string, OdinDataSort>;
  fieldFilters?: FieldFiltersProps;
  recordIds?: Array<string>;
}

export const detailsWindowApi = createApi({
  reducerPath: 'details-window',
  baseQuery: baseQueryWithReAuth,
  tagTypes: ['DetailsHeader', 'DetailsData'],
  endpoints: (builder) => ({
    detailsWindowData: builder.query<Array<Record<string, unknown>>, DetailsWindowQueryParameters>({
      query: (args) => {
        let url = `/data/${args.widgetId}/details?`;

        if (args.page) {
          url += `&page=${args.page}`;
        }
        if (args.pageSize) {
          url += `&pageSize=${args.pageSize}`;
        }
        //column chart rate
        if (args.recordIds) {
          const ids = args.recordIds.length > 0 ? args.recordIds.join(',') : '-1';
          url = url + `&filter=recordIds:in:${ids}`;
        } else {
          if (args.field && args.fieldValue) {
            url += dateValueFieldFormatter(args.field, args.fieldValue);
          }
          if (args.secondField && args.secondFieldValue) {
            url += dateValueFieldFormatter(args.secondField, args.secondFieldValue);
          }
        }
        if (args.sortedFields) {
          const { sort, orderBy } = getSortedFields(args.sortedFields);
          url += `&sort=${sort}&orderBy=${orderBy}`;
        }
        if (args.fieldFilters) {
          url += getFilters(args.fieldFilters);
        }
        return { url, method: 'GET' };
      },
      providesTags: (result, _error, { widgetId }) =>
        Array.isArray(result)
          ? [
              ...result.map((item) => ({ type: 'DetailsData', id: item.recordId as string }) as const),
              { type: 'DetailsData', id: `LIST-${widgetId}` } as const,
            ]
          : [{ type: 'DetailsData', id: `LIST-${widgetId}` } as const],
    }),
    detailsWindowHeader: builder.query<Array<TableDataColumn>, string>({
      query: (widgetId) => {
        return {
          url: `/data/${widgetId}/details/header`,
          method: 'GET',
        };
      },
      providesTags: (result, _error, widgetId) => (result ? [{ type: 'DetailsHeader', id: widgetId }] : []),
    }),
  }),
});

export const { useDetailsWindowDataQuery, useDetailsWindowHeaderQuery } = detailsWindowApi;

export const isEmptyValue = (value: string) => {
  return value === '[blank]' || value === '[none]' || value === '';
};

const getEmptyFilterValue = () => {
  return '%5Bblanks%5D';
};

export const dateValueFieldFormatter = (field: string, fieldValue: string): string => {
  const parsedDate = parse(fieldValue, 'P', new Date(), { locale: enGB });

  //MAP widget only supports `eq`
  if (field === 'lat' || field === 'lon') {
    return `&filter=${field}:eq:${fieldValue}`;
  }
  if (isValid(parsedDate)) {
    // if parseDate is a valid date that include year, month and day, the format should be given as yyyy-mm-dd
    const formattedDate = format(parsedDate, 'yyyy-MM-dd');
    return `&filter=${field}:eq:${formattedDate}`;
  } else if (isNaN(Number(fieldValue)) && isValid(new Date(fieldValue))) {
    // if filed value is a valid date that contains only year and month, the format should be given as yyyy-mm
    const formattedDate = format(new Date(fieldValue), 'yyyy-MM');
    return `&filter=${field}:like:${formattedDate}`;
  } else {
    const isBlank = isEmptyValue(fieldValue);
    return `&filter=${field}:eq:${isBlank ? getEmptyFilterValue() : fieldValue}`;
  }
};
